import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mic, Plus, Search, User, ExternalLink, Edit, Trash2, Loader2, Archive, RotateCcw, Target, ChevronUp, ChevronDown } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useArtists } from '../hooks/useArtists';
import { useContacts } from '../hooks/useContacts';
import { EnhancedArtistModal } from '../components/modals/EnhancedArtistModal';
// Keep the old import temporarily for backwards compatibility
// import { ArtistModal } from '../components/modals/ArtistModal';
import { ArtistAvatar } from '../components/artists/ArtistAvatar';
import { ArchiveModal } from '../components/shared/ArchiveModal';
import { DeleteModal } from '../components/shared/DeleteModal';
import { RestoreModal } from '../components/shared/RestoreModal';
import { useAuth } from '../hooks/useAuth';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import type { Artist } from '../../shared/types/entities';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCampaignCounts } from '../hooks/useCampaignCounts';

export const Artists: React.FC = () => {
  const { isAdmin } = useAuth();
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState<string>(searchParams.get('search') || '');
  const [modalArtist, setModalArtist] = useState<Artist | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [initialCreateData, setInitialCreateData] = useState<any | null>(null);
  const [quickAdd, setQuickAdd] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'archived'>('all');
  const [archivedArtists, setArchivedArtists] = useState<Artist[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

  const { artistCounts } = useCampaignCounts();

  // Sorting state
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');



  const { artists, loading, error, createArtist, updateArtist, archiveArtist, restoreArtist, deleteArtist, getArchivedArtists } = useArtists({
    search: searchTerm,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const { contacts } = useContacts();

  // Map client data to artists
  const artistsWithClients = useMemo(() => {
    if (!artists || !contacts) return artists || [];
    
    // Create a map of contacts by ID
    const contactsMap = new Map();
    contacts.forEach(contact => {
      contactsMap.set(contact.id, contact);
    });
    
    // Map artists with their client data
    const mapped = artists.map(artist => {
      const client = contactsMap.get(artist.client_id);
      return {
        ...artist,
        client: client || null
      };
    });
    
    return mapped;
  }, [artists, contacts]);

  // Calculate campaign count for each artist
  const getArtistCampaigns = useCallback((artist: Artist) => {
    const stats = artistCounts[artist.id];
    return {
      active: stats?.active ?? 0,
      total: stats?.total ?? 0
    };
  }, [artistCounts]);

  // Sort artists based on current sort field and direction
  const sortArtists = useCallback((artists: Artist[]) => {
    return [...artists].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'client':
          aValue = a.client?.name?.toLowerCase() || '';
          bValue = b.client?.name?.toLowerCase() || '';
          break;
        case 'campaigns':
          const aCampaigns = getArtistCampaigns(a);
          const bCampaigns = getArtistCampaigns(b);
          aValue = aCampaigns.active;
          bValue = bCampaigns.active;
          break;
        case 'followers':
          aValue = a.followers_count || 0;
          bValue = b.followers_count || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection, getArtistCampaigns]);

  // Filter and sort artists and contacts
  const filteredAndSortedResults = useMemo(() => {
    if (!artistsWithClients || !contacts) return { artists: [], contacts: [] };
    
    if (searchTerm === '') {
      return {
        artists: sortArtists(artistsWithClients),
        contacts: []
      };
    }
    
    const searchLower = searchTerm.toLowerCase();
    
    // Filter artists
    const filteredArtists = artistsWithClients.filter(artist => {
      if (!artist) return false;
      
      return artist.name?.toLowerCase().includes(searchLower) ||
             artist.client?.name?.toLowerCase().includes(searchLower);
    });
    
    // Filter contacts
    const filteredContacts = contacts.filter(contact => {
      if (!contact) return false;
      
      return contact.name?.toLowerCase().includes(searchLower);
    });
    
    return {
      artists: sortArtists(filteredArtists),
      contacts: filteredContacts.sort((a, b) => {
        const aName = a.name?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';
        return aName.localeCompare(bName);
      })
    };
  }, [artistsWithClients, contacts, searchTerm, sortArtists]);

  // Sort archived artists
  const sortedArchivedArtists = useMemo(() => {
    return sortArtists(archivedArtists);
  }, [archivedArtists, sortArtists]);


  const openCreateModal = () => {
    setModalArtist(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const openModal = (artist: Artist, mode: 'view' | 'edit' = 'view') => {
    setModalArtist(artist);
    setModalMode(mode);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setModalArtist(null);
      setModalMode('view');
    }, 300);
  };

  // Submit is only used for create flow; handled inline below if needed

  // Delete handled from drawer actions


  // Sync state to URL query params
  useEffect(() => {
    const next: Record<string, string> = {};
    if (searchTerm) next.search = searchTerm;
    setSearchParams(next, { replace: true });
  }, [searchTerm, setSearchParams]);

  // Load archived artists when switching to archived tab
  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
    if (activeTab === 'archived') {
      loadArchivedArtists();
    }
  }, [activeTab]);

  // Listen for archive/restore/delete events
  useEffect(() => {
    const handleArtistArchived = () => {
      // Artist was archived, refresh the list
      window.location.reload(); // Simple refresh for now
    };

    const handleArtistRestored = () => {
      // Artist was restored, refresh the list
      window.location.reload(); // Simple refresh for now
    };

    const handleArtistDeleted = () => {
      // Artist was deleted, refresh the list
      window.location.reload(); // Simple refresh for now
    };

    window.addEventListener('artist-archived', handleArtistArchived);
    window.addEventListener('artist-restored', handleArtistRestored);
    window.addEventListener('artist-deleted', handleArtistDeleted);

    return () => {
      window.removeEventListener('artist-archived', handleArtistArchived);
      window.removeEventListener('artist-restored', handleArtistRestored);
      window.removeEventListener('artist-deleted', handleArtistDeleted);
    };
  }, []);



  // Load archived artists
  const loadArchivedArtists = async () => {
    setArchivedLoading(true);
    try {
      const archived = await getArchivedArtists();
      setArchivedArtists(archived);
    } catch (err) {
      console.error('Failed to load archived artists:', err);
    } finally {
      setArchivedLoading(false);
    }
  };

  // Handle archive artist
  const handleArchiveArtist = async () => {
    if (!selectedArtist) return;
    try {
      await archiveArtist(selectedArtist.id);
      setShowArchiveModal(false);
      setSelectedArtist(null);
      // Refresh the artists list
      window.dispatchEvent(new CustomEvent('artist-archived', { detail: { id: selectedArtist.id } }));
    } catch (e) {
      console.error('Failed to archive artist', e);
    }
  };

  // Handle restore artist
  const handleRestoreArtist = async () => {
    if (!selectedArtist) return;
    try {
      await restoreArtist(selectedArtist.id);
      setShowRestoreModal(false);
      setSelectedArtist(null);
      // Remove from archived list
      setArchivedArtists(prev => prev.filter(a => a.id !== selectedArtist.id));
      // Refresh main artists list
      window.dispatchEvent(new CustomEvent('artist-restored', { detail: { id: selectedArtist.id } }));
    } catch (e) {
      console.error('Failed to restore artist', e);
    }
  };

  // Handle delete artist (permanent)
  const handleDeleteArtistPermanent = async () => {
    if (!selectedArtist) return;
    try {
      await deleteArtist(selectedArtist.id);
      setShowDeleteModal(false);
      setSelectedArtist(null);
      // Remove from archived list if we're on archived tab
      if (activeTab === 'archived') {
        setArchivedArtists(prev => prev.filter(a => a.id !== selectedArtist.id));
      }
      // Refresh the artists list
      window.dispatchEvent(new CustomEvent('artist-deleted', { detail: { id: selectedArtist.id } }));
    } catch (e) {
      console.error('Failed to delete artist', e);
    }
  };

  // Open archive modal
  const handleArchiveClick = (artist: Artist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedArtist(artist);
    setShowArchiveModal(true);
  };

  // Open delete modal
  const handleDeleteClick = (artist: Artist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedArtist(artist);
    setShowDeleteModal(true);
  };

  // Open restore modal
  const handleRestoreClick = (artist: Artist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedArtist(artist);
    setShowRestoreModal(true);
  };

  const parseQuickAdd = (value: string) => {
    const v = value.trim();
    if (!v) return { kind: 'empty' as const, valid: false };
    const looksLikeUrl = /^https?:\/\//i.test(v);
    const isSpotifyArtist = v.includes('open.spotify.com/artist/') || v.startsWith('spotify:artist:');
    if (isSpotifyArtist) {
      return { kind: 'spotify' as const, valid: true, spotify_url: v };
    }
    if (looksLikeUrl) {
      return { kind: 'url' as const, valid: false, error: 'Please paste a Spotify ARTIST URL' };
    }
    return { kind: 'name' as const, valid: true, name: v };
  };

  // Sorting function
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearchTerm(searchInput);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };



  // Spotify import flow removed for now; will be reintroduced directly in the drawer later

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full page-transition">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 loading-skeleton rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 loading-skeleton rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <PageHeader
          icon={<Mic className="h-8 w-8" />}
          title="Artists"
          actions={activeTab === 'all' ? (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-900"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Artist
            </button>
          ) : null}
        />



        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700/80 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { id: 'all', label: 'All Artists' },
              { id: 'archived', label: 'Archived' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'all' | 'archived')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 dark:text-purple-300'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
              placeholder="Search artists or contacts... (Press Enter to search)"
              value={searchInput}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              className={`pl-10 pr-8 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                searchTerm ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-300 dark:border-gray-600'
              }`}
              />
              {loading && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
            </div>
          
          {/* Filter Status and Instructions */}
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Showing {filteredAndSortedResults.artists.length} artist{filteredAndSortedResults.artists.length === 1 ? '' : 's'}
              {searchTerm && filteredAndSortedResults.contacts.length > 0 && (
                <span className="ml-2">and {filteredAndSortedResults.contacts.length} contact{filteredAndSortedResults.contacts.length === 1 ? '' : 's'}</span>
              )}
              {searchTerm && (
                <span className="ml-2 text-purple-600 dark:text-purple-400 font-medium">
                  (filtered by "{searchTerm}")
                </span>
              )}
            </div>
            
            {searchTerm && (
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 rounded-full">
                  <Search className="h-3 w-3 mr-1" />
                  Search: "{searchTerm}"
            </div>
            <button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchInput('');
                  }}
                  className="inline-flex items-center px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Clear search filter to show all artists"
                >
                  ✕ Clear Filter
            </button>
          </div>
              )}
            </div>
          
          {!searchTerm && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Tip: Type your search term and press Enter to filter results
          </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
            <div className="text-red-800 dark:text-red-200">{error}</div>
          </div>
        )}

        {/* Search Results */}
        <div className="space-y-6">
          {/* Contacts Section (only when searching) */}
          {searchTerm && filteredAndSortedResults.contacts.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Contacts ({filteredAndSortedResults.contacts.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSortedResults.contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => {
                      // Navigate to contacts page or open contact modal
                      console.log('Contact clicked:', contact);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {contact.name || 'Untitled Contact'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Contact
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
        )}

        {/* Artists List */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
          {activeTab === 'all' && (
            <>
              {artists.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Mic className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No artists found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {searchTerm 
                      ? `No artists match your search for "${searchTerm}". ${filteredAndSortedResults.contacts.length > 0 ? 'However, we found some contacts that match.' : 'Try a different search term or clear the filter to see all artists.'}` 
                      : 'Get started by adding your first artist.'}
                  </p>
                  {searchTerm ? (
                    <div className="space-y-3">
                    <button
                        onClick={() => {
                          setSearchTerm('');
                          setSearchInput('');
                        }}
                      className="inline-flex items-center px-4 py-2 border rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        ✕ Clear Search Filter
                    </button>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        This will show all {artists.length} artists
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-lg mx-auto space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={quickAdd}
                          onChange={(e) => setQuickAdd(e.target.value)}
                          placeholder="Paste Spotify URL or type a name..."
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <button
                          onClick={() => {
                            const parsed = parseQuickAdd(quickAdd);
                            if (!quickAdd.trim()) return openCreateModal();
                            if (!parsed.valid) {
                              if ((parsed as any).error) toast.error((parsed as any).error);
                              return;
                            }
                            const init: any = {};
                            if (parsed.kind === 'spotify') init.spotify_url = (parsed as any).spotify_url;
                            if (parsed.kind === 'name') init.name = (parsed as any).name;
                            setInitialCreateData(init);
                            setModalArtist(null);
                            setModalMode('create');
                            setModalOpen(true);
                          }}
                          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                          <Plus className="h-4 w-4 mr-2" /> Quick Add
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={openCreateModal}
                          className="text-sm text-gray-600 dark:text-gray-400 underline"
                        >
                          Or open full add form
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {filteredAndSortedResults.artists.filter(Boolean).map((artist) => {
                      return (
                        <div
                          key={artist!.id}
                          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={() => openModal(artist as any, 'view')}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <ArtistAvatar artist={artist as any} size={48} />
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                  {artist!.name}
                                  {artist!.spotify_url && (
                                    <a
                                      href={artist!.spotify_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-green-500 hover:text-green-600"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {artist!.followers_count && artist!.followers_count > 0 ? (
                                    <span>{artist!.followers_count.toLocaleString()} followers</span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Contact</div>
                              <div className="text-sm text-gray-900 dark:text-white">{artist!.client?.name || 'Unknown Contact'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Campaigns</div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {(() => {
                                  const campaigns = getArtistCampaigns(artist as any);
                                  return `${campaigns.active} active`;
                                })()}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-end">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal(artist as any, 'edit');
                                }}
                                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                title="Edit artist"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveClick(artist as any, e);
                                }}
                                className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                                title="Archive artist"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(artist as any, e);
                                  }}
                                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                  title="Delete artist"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-1">
                            Artist
                              {sortField === 'name' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('client')}
                          >
                            <div className="flex items-center gap-1">
                              Contact
                              {sortField === 'client' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('campaigns')}
                          >
                            <div className="flex items-center gap-1">
                              Campaigns
                              {sortField === 'campaigns' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredAndSortedResults.artists.filter(Boolean).map((artist) => (
                        <tr
                          key={artist!.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => openModal(artist as any, 'view')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openModal(artist as any, 'view');
                            }
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <ArtistAvatar artist={artist as any} size={40} />
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {artist!.name}
                                  {artist!.spotify_url && (
                                    <a
                                      href={artist!.spotify_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 text-green-500 hover:text-green-600"
                                    >
                                      <ExternalLink className="h-3 w-3 inline" />
                                    </a>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {artist!.followers_count && artist!.followers_count > 0 ? (
                                    <span className="ml-2">• {artist!.followers_count.toLocaleString()} followers</span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              <User className="h-3 w-3 mr-1" />
                              {artist!.client?.name || 'Unknown Contact'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const campaigns = getArtistCampaigns(artist as any);
                              return (
                                <div className="flex items-center gap-2">
                                  <Target className="h-4 w-4 text-purple-600" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {campaigns.active} active
                                  </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {campaigns.total} total campaigns
                                  </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => openModal(artist as any, 'edit')}
                                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                title="Edit artist"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleArchiveClick(artist as any, e)}
                                className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                                title="Archive artist"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                                <button
                                  onClick={(e) => handleDeleteClick(artist as any, e)}
                                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                  title="Delete artist"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </>
          )}
          </div>

          {activeTab === 'archived' && (
            <>
              {archivedLoading ? (
                <div className="px-6 py-12 text-center">
                  <Loader2 className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading archived artists...</p>
                </div>
              ) : archivedArtists.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Archive className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No archived artists</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Archived artists will appear here when you archive them.
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile Card View for Archived Artists */}
                  <div className="md:hidden space-y-4">
                    {sortedArchivedArtists.map((artist) => (
                      <div
                        key={artist.id}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => openModal(artist, 'view')}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <ArtistAvatar artist={artist} size={48} />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {artist.name}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Contact</div>
                            <div className="text-sm text-gray-900 dark:text-white">{artist.client?.name || 'Unknown Contact'}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Campaigns</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {(() => {
                                const campaigns = getArtistCampaigns(artist);
                                return `${campaigns.active} active`;
                              })()}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-end">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal(artist, 'edit');
                              }}
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              title="Edit artist"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestoreClick(artist, e);
                              }}
                              className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                              title="Restore artist"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(artist, e);
                                }}
                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                title="Delete permanently"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View for Archived Artists */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-1">
                            Artist
                              {sortField === 'name' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('client')}
                          >
                            <div className="flex items-center gap-1">
                              Contact
                              {sortField === 'client' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => handleSort('campaigns')}
                          >
                            <div className="flex items-center gap-1">
                              Campaigns
                              {sortField === 'campaigns' && (
                                sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedArchivedArtists.map((artist) => (
                          <tr
                            key={artist.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                            onClick={() => openModal(artist, 'view')}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <ArtistAvatar artist={artist} size={40} />
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {artist.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <User className="h-3 w-3 mr-1" />
                                {artist.client?.name || 'Unknown Contact'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const campaigns = getArtistCampaigns(artist);
                                return (
                                  <div className="flex items-center gap-2">
                                    <Target className="h-4 w-4 text-purple-600" />
                                    <div>
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {campaigns.active} active
                              </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {campaigns.total} total campaigns
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => openModal(artist, 'edit')}
                                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                  title="Edit artist"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => handleRestoreClick(artist, e)}
                                  className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                                  title="Restore artist"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={(e) => handleDeleteClick(artist, e)}
                                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                    title="Delete permanently"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        </div>
      </div>

      {/* Enhanced Artist Modal */}
      <EnhancedArtistModal
        artist={modalArtist}
        isOpen={modalOpen}
        onClose={closeModal}
        clients={contacts}
        onUpdate={async (id, updates) => {
          // Reuse hook's update for optimistic update
          const updated = await updateArtist(id, updates);
          return updated;
        }}
        onArchive={async (id) => {
          await archiveArtist(id);
          closeModal();
          toast.success('Artist archived', { duration: 1500 });
        }}
        onRestore={async (id) => {
          await restoreArtist(id);
          closeModal();
          toast.success('Artist restored', { duration: 1500 });
        }}
        mode={modalMode}
        onCreate={async (data) => {
          const created = await createArtist(data);
          return created;
        }}
        onCreated={(created) => {
          closeModal();
          toast.success(`Artist "${created.name}" created successfully!`);
        }}
        initialCreateData={initialCreateData || undefined}
      />

      {/* Modals */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setSelectedArtist(null);
        }}
        onArchive={handleArchiveArtist}
        entityName={selectedArtist?.name || ''}
        entityType="artist"
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedArtist(null);
        }}
        onDelete={handleDeleteArtistPermanent}
        entityName={selectedArtist?.name || ''}
        entityType="artist"
        isAdmin={effectiveHasRole('admin') || effectiveHasRole('super')}
      />

      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setSelectedArtist(null);
        }}
        onRestore={handleRestoreArtist}
        entityName={selectedArtist?.name || ''}
        entityType="artist"
      />

      {/* Spotify Import flow is temporarily disabled in this page */}
    </>
  );
};
